import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, updateProfile, changePassword, deleteAccount, logout } from '../services/authService';
import { listTokens, createToken, revokeToken, regenerateToken } from '../services/tokenService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/profile-settings.css';

const ProfileSettings = () => {
    const navigate = useNavigate();
    const { user: authUser, updateUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('personal');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

    const [profile, setProfile] = useState({
        email: '',
        emailVerified: false,
        role: '',
        createdAt: '',
        name: '',
        orcid: '',
    });

    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [tokens, setTokens] = useState([]);
    const [tokensLoading, setTokensLoading] = useState(false);
    const [tokenActionError, setTokenActionError] = useState('');
    const [tokenName, setTokenName] = useState('');
    const [tokenExpiry, setTokenExpiry] = useState('');
    const [creatingToken, setCreatingToken] = useState(false);
    const [revealedToken, setRevealedToken] = useState(null); // { token, name }
    const [revokeTargetId, setRevokeTargetId] = useState(null);
    const [regeneratingId, setRegeneratingId] = useState(null);

    useEffect(() => {
        loadProfile();
    }, []);

    useEffect(() => {
        if (activeTab === 'tokens') {
            loadTokens();
        }
    }, [activeTab]);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const data = await getUserProfile();
            setProfile(data);
        } catch (err) {
            setError(err.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleProfileChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProfile(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');
        setSaving(true);

        try {
            const profileData = {
                name: profile.name,
                orcid: profile.orcid,
            };

            await updateProfile(profileData);
            updateUser({ name: profileData.name });
            setMessage('Profile updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setMessage('');
        setError('');

        if (passwords.newPassword !== passwords.confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (passwords.newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setSaving(true);

        try {
            await changePassword(passwords.currentPassword, passwords.newPassword);
            setMessage('Password changed successfully!');
            setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmationText !== 'DELETE MY ACCOUNT') {
            return;
        }

        try {
            setSaving(true);
            await deleteAccount();
            await logout();
            window.location.href = '/login';
        } catch (err) {
            setError(err.message || 'Failed to delete account');
            setSaving(false);
            setShowDeleteModal(false);
        }
    };

    const loadTokens = async () => {
        try {
            setTokensLoading(true);
            setTokenActionError('');
            const data = await listTokens();
            setTokens(data);
        } catch (err) {
            setTokenActionError(err.message || 'Failed to load API tokens');
        } finally {
            setTokensLoading(false);
        }
    };

    const handleCreateToken = async (e) => {
        e.preventDefault();
        setTokenActionError('');

        if (!tokenName.trim()) {
            setTokenActionError('Token name is required');
            return;
        }

        setCreatingToken(true);
        try {
            const response = await createToken({
                name: tokenName.trim(),
                expiresInDays: tokenExpiry ? parseInt(tokenExpiry, 10) : null,
            });
            setRevealedToken({ token: response.token, name: response.name });
            setTokenName('');
            setTokenExpiry('');
            await loadTokens();
        } catch (err) {
            setTokenActionError(err.message || 'Failed to create token');
        } finally {
            setCreatingToken(false);
        }
    };

    const handleConfirmRevoke = async () => {
        if (!revokeTargetId) return;
        try {
            setSaving(true);
            await revokeToken(revokeTargetId);
            setRevokeTargetId(null);
            await loadTokens();
        } catch (err) {
            setTokenActionError(err.message || 'Failed to revoke token');
        } finally {
            setSaving(false);
        }
    };

    const handleRegenerateToken = async (id) => {
        setTokenActionError('');
        setRegeneratingId(id);
        try {
            const response = await regenerateToken(id);
            setRevealedToken({ token: response.token, name: response.name });
            await loadTokens();
        } catch (err) {
            setTokenActionError(err.message || 'Failed to regenerate token');
        } finally {
            setRegeneratingId(null);
        }
    };

    const handleCopyToken = () => {
        if (revealedToken) {
            navigator.clipboard.writeText(revealedToken.token);
        }
    };

    // Inline Styles
    const styles = {
        container: {
            maxWidth: '1000px',
            margin: '40px auto',
            padding: '20px',
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            minHeight: '600px',
        },
        sidebar: {
            display: 'flex',
            flexDirection: 'column',
        },
        sidebarItem: (active) => ({
            padding: '12px 16px',
            borderRadius: '8px',
            backgroundColor: active ? '#ebf4ff' : 'transparent',
            color: active ? '#2b6cb0' : '#4a5568',
            fontWeight: active ? '600' : '500',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '8px',
            border: 'none',
            textAlign: 'left',
            fontSize: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        }),
        content: {
            flex: 1,
            paddingTop: '10px',
            minWidth: 0,
        },
        heading: {
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '24px',
        },
        subHeading: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '20px',
        },
        formGroup: {
            marginBottom: '20px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#4a5568',
            marginBottom: '8px',
        },
        input: {
            width: '100%',
            padding: '10px 12px',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            fontSize: '15px',
            color: '#2d3748',
            transition: 'border-color 0.2s',
            outline: 'none',
        },
        button: {
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: 'none',
        },
        primaryButton: {
            backgroundColor: '#3182ce',
            color: '#ffffff',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
        secondaryButton: {
            backgroundColor: '#edf2f7',
            color: '#4a5568',
            marginTop: '20px',
        },
        infoBox: {
            padding: '16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '1px solid #edf2f7',
        },
        infoItem: {
            marginBottom: '8px',
            fontSize: '14px',
            color: '#4a5568',
        },
        alert: (type) => ({
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px',
            fontWeight: '500',
            backgroundColor: type === 'error' ? '#fff5f5' : '#f0fff4',
            color: type === 'error' ? '#c53030' : '#276749',
            border: `1px solid ${type === 'error' ? '#feb2b2' : '#9ae6b4'}`,
        }),
        dangerZone: {
            marginTop: '60px',
            paddingTop: '24px',
            borderTop: '1px solid #edf2f7',
        },
        dangerText: {
            fontSize: '14px',
            color: '#718096',
            marginBottom: '16px',
        },
        deleteBtn: {
            color: '#e53e3e',
            background: 'none',
            border: 'none',
            padding: '0',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            textDecoration: 'underline',
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
        },
        modalContent: {
            backgroundColor: '#fff',
            padding: '32px',
            borderRadius: '12px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <p>Loading profile...</p>
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '20px 0' }}>
            <div style={styles.container} className="profile-settings-container">
                {/* Sidebar Navigation */}
                <div style={styles.sidebar} className="profile-settings-sidebar">
                    <h3 style={{ ...styles.subHeading, padding: '0 16px', marginBottom: '24px' }}>Settings</h3>
                    <button
                        style={styles.sidebarItem(activeTab === 'personal')}
                        onClick={() => setActiveTab('personal')}
                    >
                        Personal Information
                    </button>
                    <button
                        style={styles.sidebarItem(activeTab === 'security')}
                        onClick={() => setActiveTab('security')}
                    >
                        Security & Password
                    </button>
                    <button
                        style={styles.sidebarItem(activeTab === 'account')}
                        onClick={() => setActiveTab('account')}
                    >
                        Account Management
                    </button>
                    <button
                        style={styles.sidebarItem(activeTab === 'tokens')}
                        onClick={() => setActiveTab('tokens')}
                    >
                        API Tokens
                    </button>

                    <div style={{ marginTop: 'auto' }}>
                        <button
                            style={styles.sidebarItem(false)}
                            onClick={() => navigate('/')}
                        >
                            Back to Home
                        </button>
                    </div>
                </div>

                {/* Main Content Area */}
                <div style={styles.content} className="profile-settings-content">
                    {message && (
                        <div style={styles.alert('success')}>
                            {message}
                        </div>
                    )}

                    {error && (
                        <div style={styles.alert('error')}>
                            {error}
                        </div>
                    )}

                    {/* Personal Info Tab */}
                    {activeTab === 'personal' && (
                        <div>
                            <h2 style={styles.heading}>Personal Information</h2>

                            <div style={styles.infoBox}>
                                <div style={styles.infoItem}>
                                    <strong>Email:</strong> {profile.email}
                                    {profile.emailVerified && <span style={{ color: '#38a169', marginLeft: '8px', fontSize: '12px', fontWeight: 'bold' }}>✓ Verified</span>}
                                </div>
                                <div style={styles.infoItem}>
                                    <strong>Member since:</strong> {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
                                </div>
                            </div>

                            <form onSubmit={handleSaveProfile}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={profile.name || ''}
                                        onChange={handleProfileChange}
                                        style={styles.input}
                                        placeholder="John Doe"
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>ORCID iD</label>
                                    <input
                                        type="text"
                                        name="orcid"
                                        value={profile.orcid || ''}
                                        onChange={handleProfileChange}
                                        style={styles.input}
                                        placeholder="0000-0000-0000-0000"
                                    />
                                    <small style={{ color: '#718096', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                        Providing your ORCID helps link your research data correctly.
                                    </small>
                                </div>

                                <button
                                    type="submit"
                                    style={{ ...styles.button, ...styles.primaryButton }}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Update Profile'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div>
                            <h2 style={styles.heading}>Security</h2>
                            <p style={{ ...styles.infoItem, marginBottom: '32px' }}>
                                Update your password to keep your account secure.
                            </p>

                            <form onSubmit={handleChangePassword}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Current Password</label>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        value={passwords.currentPassword}
                                        onChange={handlePasswordChange}
                                        style={styles.input}
                                        required
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>New Password</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwords.newPassword}
                                        onChange={handlePasswordChange}
                                        style={styles.input}
                                        required
                                        minLength="8"
                                    />
                                    <small style={{ color: '#718096', fontSize: '12px' }}>At least 8 characters.</small>
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Confirm New Password</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwords.confirmPassword}
                                        onChange={handlePasswordChange}
                                        style={styles.input}
                                        required
                                    />
                                </div>

                                <button
                                    type="submit"
                                    style={{ ...styles.button, ...styles.primaryButton }}
                                    disabled={saving}
                                >
                                    {saving ? 'Changing...' : 'Change Password'}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Account Tab */}
                    {activeTab === 'account' && (
                        <div>
                            <h2 style={styles.heading}>Account Management</h2>

                            <div style={styles.dangerZone}>
                                <h4 style={{ color: '#2d3748', marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>Delete Account</h4>
                                <p style={styles.dangerText}>
                                    Deleting your account is permanent and cannot be undone.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowDeleteModal(true)}
                                    style={styles.deleteBtn}
                                >
                                    Delete my account permanently
                                </button>
                            </div>
                        </div>
                    )}

                    {/* API Tokens Tab */}
                    {activeTab === 'tokens' && (
                        <div>
                            <h2 style={styles.heading}>API Tokens</h2>
                            <p style={{ ...styles.infoItem, marginBottom: '32px' }}>
                                Personal access tokens let you authenticate write requests from the REST API or the
                                Python package. Treat a token like a password — anyone with it can submit data as you.
                            </p>

                            {tokenActionError && (
                                <div style={styles.alert('error')}>{tokenActionError}</div>
                            )}

                            <form onSubmit={handleCreateToken} style={{ ...styles.infoBox, marginBottom: '32px' }}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 2, minWidth: '200px' }}>
                                        <label style={styles.label}>Token name</label>
                                        <input
                                            type="text"
                                            value={tokenName}
                                            onChange={(e) => setTokenName(e.target.value)}
                                            style={styles.input}
                                            placeholder="e.g. Lab laptop, CI pipeline"
                                        />
                                    </div>
                                    <div style={{ flex: 1, minWidth: '160px' }}>
                                        <label style={styles.label}>Expires</label>
                                        <select
                                            value={tokenExpiry}
                                            onChange={(e) => setTokenExpiry(e.target.value)}
                                            style={styles.input}
                                        >
                                            <option value="">No expiry</option>
                                            <option value="30">30 days</option>
                                            <option value="90">90 days</option>
                                            <option value="365">365 days</option>
                                        </select>
                                    </div>
                                    <button
                                        type="submit"
                                        style={{ ...styles.button, ...styles.primaryButton }}
                                        disabled={creatingToken}
                                    >
                                        {creatingToken ? 'Creating...' : 'Create token'}
                                    </button>
                                </div>
                            </form>

                            {tokensLoading ? (
                                <p style={styles.infoItem}>Loading tokens...</p>
                            ) : tokens.length === 0 ? (
                                <p style={styles.infoItem}>You don't have any API tokens yet.</p>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', borderBottom: '2px solid #edf2f7' }}>
                                                <th style={{ padding: '10px 8px', color: '#718096' }}>Name</th>
                                                <th style={{ padding: '10px 8px', color: '#718096' }}>Token</th>
                                                <th style={{ padding: '10px 8px', color: '#718096' }}>Status</th>
                                                <th style={{ padding: '10px 8px', color: '#718096' }}>Created</th>
                                                <th style={{ padding: '10px 8px', color: '#718096' }}>Last used</th>
                                                <th style={{ padding: '10px 8px', color: '#718096' }}>Expires</th>
                                                <th style={{ padding: '10px 8px', color: '#718096' }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tokens.map((token) => (
                                                <tr key={token.id} style={{ borderBottom: '1px solid #f7fafc' }}>
                                                    <td style={{ padding: '10px 8px', color: '#2d3748' }}>{token.name}</td>
                                                    <td style={{ padding: '10px 8px', fontFamily: 'monospace', color: '#718096' }}>
                                                        {token.tokenPrefix}&hellip;
                                                    </td>
                                                    <td style={{ padding: '10px 8px' }}>
                                                        <span style={{
                                                            color: token.status === 'ACTIVE' ? '#276749' : '#c53030',
                                                            fontWeight: '600',
                                                            fontSize: '12px',
                                                        }}>
                                                            {token.status === 'ACTIVE' ? 'Active' : 'Revoked'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '10px 8px', color: '#718096' }}>
                                                        {token.createdAt ? new Date(token.createdAt).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td style={{ padding: '10px 8px', color: '#718096' }}>
                                                        {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : 'Never'}
                                                    </td>
                                                    <td style={{ padding: '10px 8px', color: '#718096' }}>
                                                        {token.expiresAt ? new Date(token.expiresAt).toLocaleDateString() : 'Never'}
                                                    </td>
                                                    <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                        {token.status === 'ACTIVE' && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRegenerateToken(token.id)}
                                                                    disabled={regeneratingId === token.id}
                                                                    style={{ ...styles.deleteBtn, color: '#3182ce', marginRight: '16px' }}
                                                                >
                                                                    {regeneratingId === token.id ? 'Regenerating...' : 'Regenerate'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setRevokeTargetId(token.id)}
                                                                    style={styles.deleteBtn}
                                                                >
                                                                    Revoke
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Reveal-once token modal (shown right after create/regenerate) */}
            {revealedToken && (
                <div style={styles.modalOverlay}>
                    <div style={{ ...styles.modalContent, maxWidth: '550px' }}>
                        <h3 style={{ ...styles.subHeading, color: '#1a202c' }}>Token created</h3>
                        <p style={{ fontSize: '14px', color: '#4a5568', lineHeight: '1.6', marginBottom: '16px' }}>
                            Copy <strong>{revealedToken.name}</strong> now — for your security, you won't be able to
                            see it again.
                        </p>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: '#f7fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '10px 12px',
                            marginBottom: '24px',
                        }}>
                            <code style={{ flex: 1, fontSize: '13px', wordBreak: 'break-all', color: '#2d3748' }}>
                                {revealedToken.token}
                            </code>
                            <button
                                type="button"
                                onClick={handleCopyToken}
                                style={{ ...styles.button, backgroundColor: '#edf2f7', color: '#4a5568', padding: '6px 12px', fontSize: '13px' }}
                            >
                                Copy
                            </button>
                        </div>
                        <button
                            type="button"
                            style={{ ...styles.button, ...styles.primaryButton, width: '100%' }}
                            onClick={() => setRevealedToken(null)}
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}

            {/* Revoke Confirmation Modal */}
            {revokeTargetId && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={{ ...styles.subHeading, color: '#1a202c' }}>Revoke token</h3>
                        <p style={{ fontSize: '14px', color: '#4a5568', lineHeight: '1.6', marginBottom: '24px' }}>
                            Any application using this token will immediately lose write access. This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                style={{ ...styles.button, backgroundColor: '#edf2f7', color: '#4a5568', flex: 1 }}
                                onClick={() => setRevokeTargetId(null)}
                            >
                                Cancel
                            </button>
                            <button
                                style={{ ...styles.button, backgroundColor: '#e53e3e', color: '#ffffff', flex: 1 }}
                                disabled={saving}
                                onClick={handleConfirmRevoke}
                            >
                                {saving ? 'Revoking...' : 'Revoke token'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={{ ...styles.subHeading, color: '#1a202c' }}>Confirm Account Deletion</h3>
                        <p style={{ fontSize: '14px', color: '#4a5568', lineHeight: '1.6', marginBottom: '24px' }}>
                            All your data will be permanently removed. This action is irreversible.
                            To proceed, please type <strong>DELETE MY ACCOUNT</strong> below.
                        </p>

                        <div style={styles.formGroup}>
                            <input
                                type="text"
                                value={deleteConfirmationText}
                                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                                style={styles.input}
                                placeholder="DELETE MY ACCOUNT"
                                autoFocus
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
                            <button
                                style={{ ...styles.button, backgroundColor: '#edf2f7', color: '#4a5568', flex: 1 }}
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteConfirmationText('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    ...styles.button,
                                    backgroundColor: deleteConfirmationText === 'DELETE MY ACCOUNT' ? '#e53e3e' : '#cbd5e0',
                                    color: '#ffffff',
                                    flex: 1,
                                    cursor: deleteConfirmationText === 'DELETE MY ACCOUNT' ? 'pointer' : 'not-allowed'
                                }}
                                disabled={deleteConfirmationText !== 'DELETE MY ACCOUNT' || saving}
                                onClick={handleDeleteAccount}
                            >
                                {saving ? 'Deleting...' : 'Delete Permanently'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileSettings;
